// ── Cohorts ("/cohorts") — the cohort directory (Wave 3) ────────────────────
// ONE honest read (the cohorts list) routed through useHonestQuery ->
// <HonestState>. The page NEVER hand-rolls state branching: a gate /
// permission-denied / unavailable / error response renders via <HonestState>
// (tagged data-honest-posture), NEVER as a plain empty table. Real content (the
// DataTable + filter/create toolbar) renders only on the ready-with-data (or
// honest-empty) path.
//
// FR-COHORT-001 (company/status dropdown fix) + cohort-side PROGRAM ASSIGNMENT
// are both wired through the CohortFormModal: Company is an editable AsyncSelect,
// Status an editable Select, Program a Select of active programs (setting
// program_id IS the program → cohort assignment). All persist via
// cohorts.create/update with a UUID Idempotency-Key.
//
// ROLE-GATING (blueprint §6 release-critical):
//  - Super Admin / Admin / Coach -> full cohort CRUD + editable dropdowns +
//    complete (capabilities.ts: cohorts:create/update/complete = operationalWrite
//    roles). The server re-applies the coach assigned-only scope.
//  - Company Admin -> read-only company cohorts (no create/edit/complete).
//  - Cohort Lead -> assigned cohorts read-only (route grants VIEW; every write
//    capability is denied).
//  - PARTICIPANT -> DENIED THE COHORTS MODULE: the route manifest's `cohorts`
//    route uses cohortReadRoles which EXCLUDES participant — so participant gets
//    generic-not-found before this page mounts AND has no Cohorts nav item.
//    Participants reach their OWN cohorts only via My Cohorts (Wave 3) / the
//    Participant Dashboard (Wave 9), never this operational module.
//
// Affordances render ONLY when the read is READY (posture null) or honest-EMPTY
// (so filters can still be adjusted); a hard-denied read shows ONLY the honest
// state — no create/edit/complete controls hanging over a refused surface.

import { Button, CollectionToolbar, IconUsers, PageFrame, ProTable, useToast } from "@vjal/ui";
import { useCallback, useMemo, useState } from "react";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import { useCohortLifecycleMutations } from "../../hooks/useCoachingQueries";
import { evaluateActionCapability } from "../../domain/capabilities";
import { asLiveApiId } from "../../hooks/useLiveProductData";
import type { CoachingCohortDto, CoachingOperationResult, ListResponse } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { useCohortsCollection } from "./cohorts-collection";
import { CohortFormModal, type CohortFormValues } from "./cohort-form-modal";
import { CohortCompleteModal } from "./cohort-complete-modal";
import { UseCaseRepositoryPanel } from "../product-z6-use-case-repository";

const services = getCoachingServices();

export function CohortsPage({ role, routePath }: ProductScreenContext) {
  const toast = useToast();
  const mutations = useCohortLifecycleMutations();

  const allowCreate = evaluateActionCapability(role, "cohorts:create").allowed;
  const allowUpdate = evaluateActionCapability(role, "cohorts:update").allowed;
  const allowComplete = evaluateActionCapability(role, "cohorts:complete").allowed;

  // The /cohorts/new route reuses this page with the create modal opened (mirrors
  // the gold-pattern modal-based create; an authorized role lands straight in it).
  const startOnCreate = allowCreate && (routePath ?? "").split("?")[0].endsWith("/new");

  const [createOpen, setCreateOpen] = useState(startOnCreate);
  const [editTarget, setEditTarget] = useState<CoachingCohortDto | null>(null);
  const [completeTarget, setCompleteTarget] = useState<CoachingCohortDto | null>(null);
  const [completeResult, setCompleteResult] = useState<CoachingOperationResult | null>(null);

  const openComplete = useCallback((row: CoachingCohortDto) => {
    setCompleteResult(null);
    setCompleteTarget(row);
  }, []);

  // ── The one honest read ─────────────────────────────────────────────────────
  const query = useHonestQuery<ListResponse<CoachingCohortDto>>(
    coachingQueryKeys.list("cohorts.list"),
    () => services.cohorts.list()
  );
  const items = query.data?.items;
  const allRows = useMemo(() => items ?? [], [items]);
  const collectionActions = useMemo(
    () => ({
      allowUpdate,
      allowComplete,
      onEdit: setEditTarget,
      onComplete: openComplete
    }),
    [allowUpdate, allowComplete, openComplete]
  );
  const collection = useCohortsCollection(allRows, collectionActions, routePath);

  // useHonestQuery flags an empty `{items:[]}` as "empty"; once filters narrow a
  // non-empty list to zero we render the empty body ourselves (never a blank).
  const posture =
    query.posture === null && collection.total === 0 && allRows.length > 0 ? "empty" : query.posture;
  const gated = posture !== null;
  const showToolbar = query.posture === null || posture === "empty";

  // ── Writes (each toasts ONLY after the backend confirms; no false success) ──
  function submitCreate(values: CohortFormValues) {
    return mutations.create.mutateAsync(cohortBody(values, { forCreate: true })).then((cohort) => {
      setCreateOpen(false);
      mutations.create.reset();
      const name = cohort.name ?? values.name;
      toast.success(cohort.replayed
        ? "Cohort already exists — no duplicate created"
        : cohort.outcome === "reused"
          ? `Existing cohort ${name} reused`
          : cohort.outcome === "restored"
            ? `Existing cohort ${name} restored`
            : `Cohort ${name} created`);
      return cohort;
    });
  }

  function submitEdit(values: CohortFormValues) {
    if (!editTarget) return Promise.reject(new Error("No cohort selected"));
    return mutations.update
      .mutateAsync({ cohortId: editTarget.id, body: cohortBody(values) })
      .then((cohort) => {
        setEditTarget(null);
        mutations.update.reset();
        toast.success(`Cohort ${cohort.name ?? values.name} updated`);
        return cohort;
      });
  }

  function submitComplete(): Promise<CoachingOperationResult> {
    if (!completeTarget) return Promise.reject(new Error("No cohort selected"));
    return mutations.complete.mutateAsync(completeTarget.id).then((result) => {
      setCompleteResult(result);
      // The server returns a SOFT refusal (ok:false) when sessions are not all
      // done — only toast + close on a confirmed completion (no false success).
      if (result.ok) {
        const name = completeTarget.name;
        setCompleteTarget(null);
        mutations.complete.reset();
        toast.success(`Cohort ${name ?? ""} completed`.trim());
      }
      return result;
    });
  }

  return (
    <PageFrame title="Cohorts" subtitle="Delivery cohorts in your operational scope." className="col-span-12">
      <section aria-label="Cohorts" className="flex flex-col gap-4" data-testid="cohorts-page">
        {showToolbar && gated ? (
          <CollectionToolbar
            collection={collection}
            searchPlaceholder="Search by name, company, program, or coach…"
            primaryAction={
              allowCreate ? (
                <Button variant="primary" onClick={() => setCreateOpen(true)}>
                  Add cohort
                </Button>
              ) : undefined
            }
            className="rounded-xl border border-line"
            stableChipNames
          />
        ) : null}

        <div className="rounded-xl border border-line bg-white">
          {gated ? (
            <HonestState
              posture={posture ?? "loading"}
              gate={query.gate}
              reference={query.reference}
              onRetry={query.retry}
              icon={<IconUsers size={22} />}
              canCreate={allowCreate && !showToolbar}
              title={posture === "empty" ? "No cohorts yet" : undefined}
              message={
                posture === "empty"
                  ? allowCreate
                    ? "Create the first cohort to start delivery."
                    : "Cohorts will appear here once they are created."
                  : undefined
              }
              createAction={
                allowCreate ? (
                  <Button variant="primary" onClick={() => setCreateOpen(true)}>
                    Add cohort
                  </Button>
                ) : undefined
              }
            />
          ) : (
            /* F3: real @vjal/ui ProTable-1 recipe export under Page-1 shell. */
            <ProTable
              collection={collection}
              primaryAction={
                allowCreate ? (
                  <Button variant="primary" onClick={() => setCreateOpen(true)}>
                    Add cohort
                  </Button>
                ) : undefined
              }
              className="p-0"
              tableProps={{
                caption: "Cohorts in the current view",
                emptyTitle: "No cohorts match your search",
                emptyMessage: "Clear or change the filters above to widen the view.",
                tableClassName: "min-w-[960px]",
              }}
              stableChipNames
            />
          )}
        </div>

        <UseCaseRepositoryPanel role={role} />

      {allowCreate && (
        <CohortFormModal
          open={createOpen}
          mode="create"
          onClose={() => {
            setCreateOpen(false);
            mutations.create.reset();
          }}
          onSubmit={submitCreate}
          submitting={mutations.create.isPending}
          submitError={mutations.create.error}
        />
      )}

      {allowUpdate && (
        <CohortFormModal
          open={editTarget !== null}
          mode="edit"
          cohort={editTarget}
          onClose={() => {
            setEditTarget(null);
            mutations.update.reset();
          }}
          onSubmit={submitEdit}
          submitting={mutations.update.isPending}
          submitError={mutations.update.error}
        />
      )}

      {allowComplete && (
        <CohortCompleteModal
          open={completeTarget !== null}
          cohort={completeTarget}
          onClose={() => {
            setCompleteTarget(null);
            setCompleteResult(null);
            mutations.complete.reset();
          }}
          onConfirm={submitComplete}
          submitting={mutations.complete.isPending}
          submitError={mutations.complete.error}
          lastResult={completeResult}
        />
      )}
      </section>
    </PageFrame>
  );
}

/** Build the create/edit body — snake_case ids, integer-coerced for the live API. */
function cohortBody(values: CohortFormValues, opts?: { forCreate?: boolean }): Record<string, unknown> {
  const body: Record<string, unknown> = { name: values.name, status: values.status };
  if (values.companyId) body.company_id = asLiveApiId(values.companyId);
  if (values.programId) body.program_id = asLiveApiId(values.programId);
  if (values.coachId) body.coach_user_id = asLiveApiId(values.coachId);
  if (opts?.forCreate) {
    const start = new Date();
    start.setUTCDate(start.getUTCDate() + 7);
    body.start_date = start.toISOString().slice(0, 10);
    body.meeting_start_time = "09:00";
    body.creation_timezone =
      typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York" : "America/New_York";
  }
  return body;
}
